// The key that makes a payment idempotent (the server accepts 1 to 64 characters). A modal makes one
// when it opens and sends it with every submit, so a double click or a retry cannot pay twice.
import { httpStatusOf } from "@/core/http/HttpError";

interface UuidSource {
  randomUUID?: () => string;
}

const fallback = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 12)}`;

export const newRequestKey = (source: UuidSource | undefined = typeof crypto === "undefined" ? undefined : crypto): string =>
  source && typeof source.randomUUID === "function" ? source.randomUUID() : fallback();

/** A request key (TD-077), and whether a submit sent with it may have been recorded. */
export interface RequestKey {
  id: string;
  /** A submit with this key got no answer, or a 5xx: it may be recorded, so the key stays for this open. */
  unsure: boolean;
}

/**
 * The key after a failed submit. The server compares a replay by its amount, day and invoice, never by the lines' names, so
 * a recorded key answers the first payment for an edited body. A refusal (an answer below 500) recorded nothing: the next
 * submit takes a fresh key, and an edited body is never answered as the refused one. No answer, or a gateway's 5xx while
 * the server may still commit, keeps the key for good: a repeat answers that payment and a changed amount is refused,
 * where a fresh key would pay twice.
 */
export function keyAfterFailure(key: RequestKey, error: unknown, mint: () => string = newRequestKey): RequestKey {
  const status = httpStatusOf(error);
  return key.unsure || status === null || status >= 500 ? { id: key.id, unsure: true } : { id: mint(), unsure: false };
}
